import {
	IconAiAgents,
	IconBriefcase,
	IconCalendarBolt,
	IconCertificate2,
	IconCloud,
	IconDashboard,
	IconHistory,
	IconLibrary,
	IconList,
	IconSettings,
	IconTemplate,
} from "@tabler/icons-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import type { MenuProps } from "antd";
import { Badge, Menu, theme } from "antd";
import { useApprovals } from "@/common/artemis-operations/approvals";
import { useIsWorkspaceAdmin } from "@/common/artemis-operations/members";
import {
	useIsOrgAdmin,
	useRouteOrgSlug,
} from "@/common/artemis-operations/organization";
import type { Workspace } from "@/common/model";

type NavItem = NonNullable<MenuProps["items"]>[number];

// Non-path key so clicking the Operations parent expands it instead of navigating.
const OPS_KEY = "operations";

/** Menu label with the text on the left and an optional badge pushed to the right edge. */
function labelWithBadge(
	text: string,
	badge?: React.ReactNode,
): React.ReactNode {
	if (!badge) return text;
	return (
		<span
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
			}}
		>
			<span>{text}</span>
			{badge}
		</span>
	);
}

export function WorkspaceNav({
	workspaceId,
	collapsed,
	onNavigate,
}: {
	workspace: Workspace;
	workspaceId: string;
	collapsed?: boolean;
	onNavigate?: () => void;
}) {
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();
	const base = `/${orgSlug}/workspaces/${workspaceId}`;
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const {
		token: { colorTextTertiary },
	} = theme.useToken();

	const { isAdmin: isOrgAdmin } = useIsOrgAdmin();
	const canSeeSettings = useIsWorkspaceAdmin(workspaceId) || isOrgAdmin;

	// Live badge counts.
	const { data: approvals } = useApprovals(workspaceId);
	const pendingApprovals = (approvals ?? []).filter(
		(a) => a?.status === "PENDING",
	).length;

	const groupLabel = (text: string): React.ReactNode => (
		<span
			style={{
				fontSize: 11,
				fontWeight: 600,
				letterSpacing: "0.03em",
				color: colorTextTertiary,
			}}
		>
			{text}
		</span>
	);

	const mainItems: MenuProps["items"] = [
		{
			key: base,
			icon: <IconDashboard height="1.3em" />,
			label: "Dashboard",
		},

		{
			type: "group",
			label: groupLabel("Work"),
			children: [
				{
					key: `${base}/projects`,
					icon: <IconBriefcase height="1.3em" />,
					label: "Projects",
				},
				{
					key: `${base}/reports`,
					icon: <IconLibrary height="1.3em" />,
					label: "Reports",
				},
				{
					key: `${base}/approvals`,
					icon: <IconCertificate2 height="1.3em" />,
					label: labelWithBadge(
						"Approvals",
						pendingApprovals > 0 && (
							<Badge count={pendingApprovals} size="small" />
						),
					),
				},
				{
					key: `${base}/tasks`,
					icon: <IconList height="1.3em" />,
					label: "Tasks",
				},
				{
					key: `${base}/task-templates`,
					icon: <IconTemplate height="1.3em" />,
					label: "Templates",
				},
				{
					key: `${base}/jobs`,
					icon: <IconCalendarBolt height="1.3em" />,
					label: "Scheduled jobs",
				},
				// {
				// 	key: OPS_KEY,
				// 	icon: <IconTools height="1.3em" />,
				// 	label: "Operations",
				// 	children: [
				// 		{
				// 			key: `${base}/tasks`,
				// 			icon: <IconList height="1.3em" />,
				// 			label: "Tasks",
				// 		},
				// 		// {
				// 		// 	key: `${base}/runbooks`,
				// 		// 	icon: <IconBooks height="1.3em" />,
				// 		// 	label: "Runbooks",
				// 		// },
				// 		{
				// 			key: `${base}/jobs`,
				// 			icon: <IconCalendarBolt height="1.3em" />,
				// 			label: "Scheduled jobs",
				// 		},
				// 	],
				// },
			],
		},
		{
			type: "group",
			label: groupLabel("Infrastructure"),
			children: [
				{
					key: `${base}/agents`,
					icon: <IconAiAgents height="1.3em" />,
					label: "Agents",
				},
				{
					key: `${base}/environments`,
					icon: <IconCloud height="1.3em" />,
					label: "Environments",
				},
			],
		},
		...(canSeeSettings
			? [
					{
						type: "group" as const,
						label: groupLabel("Settings"),
						children: [
							{
								key: `${base}/audit`,
								icon: <IconHistory height="1.3em" />,
								label: "Audit log",
							},
							{
								key: `${base}/settings`,
								icon: <IconSettings height="1.3em" />,
								label: "Settings",
							},
						],
					} satisfies NavItem,
				]
			: []),
	];

	// Highlight the deepest menu key that prefixes the current path (Dashboard = exact base).
	const leafKeys = [
		base,
		`${base}/tasks`,
		`${base}/task-templates`,
		`${base}/projects`,
		`${base}/approvals`,
		`${base}/runbooks`,
		`${base}/jobs`,
		`${base}/agents`,
		`${base}/environments`,
		`${base}/reports`,
		`${base}/audit`,
		`${base}/settings`,
	];
	const selectedKey = leafKeys
		.filter((k) => pathname === k || pathname.startsWith(`${k}/`))
		.sort((a, b) => b.length - a.length)[0];
	const selectedKeys = selectedKey ? [selectedKey] : [];
	// Auto-expand the Operations submenu when one of its children is the active route.
	const opsActive =
		selectedKey === `${base}/tasks` ||
		selectedKey === `${base}/runbooks` ||
		selectedKey === `${base}/jobs`;

	const onClick: MenuProps["onClick"] = ({ key }) => {
		if (key.startsWith("/")) navigate({ to: key });
		onNavigate?.();
	};

	return (
		<Menu
			mode="inline"
			inlineCollapsed={collapsed}
			inlineIndent={12}
			selectedKeys={selectedKeys}
			defaultOpenKeys={opsActive ? [OPS_KEY] : undefined}
			onClick={onClick}
			items={mainItems}
			style={{ borderInlineEnd: "none" }}
		/>
	);
}
